<script>
import Vue from 'vue'
import { MODAL, RELOAD } from '@constants/Text'
import { GlobalBus } from '@services/bus'
import {
  backendDurationSeconds,
  formatBackendDateTime,
  formatSecondsToDuration,
} from '@utils/Format'
import { apiCrmTranscricoes } from '@modules/crm/api/apiCrmTranscricoes'
import { baixarTranscricaoTxt } from '@modules/crm/utils/transcricaoTxt'
import TranscriptClientModal from '@modules/crm/modal/transcript-client-modal/transcript-client-modal.vue'
import TranscriptLinkProjectModal from '@modules/crm/modal/transcript-link-project-modal/transcript-link-project-modal.vue'
import TranscriptParticipantsModal from '@modules/crm/modal/transcript-participants-modal/transcript-participants-modal.vue'
import Title from '@components/section-title'
import TranscriptPlatformBadge from '@modules/crm/components/transcript-platform-badge/transcript-platform-badge.vue'
import DetailMeetingHeader from '@modules/crm/components/detail-meeting-header/detail-meeting-header.vue'
import DetailTabResumo from '@modules/crm/components/detail-tab-resumo/detail-tab-resumo.vue'
import DetailTabSentimento from '@modules/crm/components/detail-tab-sentimento/detail-tab-sentimento.vue'
import DetailTabAvaliacao from '@modules/crm/components/detail-tab-avaliacao/detail-tab-avaliacao.vue'
import DetailTabAcoes from '@modules/crm/components/detail-tab-acoes/detail-tab-acoes.vue'
import DetailTabParticipantes from '@modules/crm/components/detail-tab-participantes/detail-tab-participantes.vue'
import DetailTabTranscricao from '@modules/crm/components/detail-tab-transcricao/detail-tab-transcricao.vue'

const POLL_INTERVAL = 10000
// Garante feedback visual suave mesmo quando a geração é instantânea (mesmo
// padrão de view-billing-system-overview.vue::exportToExcel).
const MIN_DOWNLOAD_LOADING_MS = 600

export default {
  name: 'TranscriptDetailModal',
  components: {
    Title,
    TranscriptPlatformBadge,
    DetailMeetingHeader,
    DetailTabResumo,
    DetailTabSentimento,
    DetailTabAvaliacao,
    DetailTabAcoes,
    DetailTabParticipantes,
    DetailTabTranscricao,
    TranscriptClientModal,
    TranscriptLinkProjectModal,
    TranscriptParticipantsModal,
  },
  data() {
    return {
      busModal: new Vue(),
      transcript: null,
      transcriptId: null,
      loading: false,
      regenerating: false,
      loadingDownload: false,
      pollHandle: null,
      tab: 'resumo',
    }
  },
  computed: {
    // Aba Avaliação — scores por critério (spec-004) lidos da store CRM,
    // indexados por id da transcrição (padrão Vuex do módulo).
    scores() {
      return this.$store.getters['crm/transcriptScores'](this.transcriptId)
    },
    loadingScores() {
      return this.$store.getters['crm/transcriptScoresLoading'](
        this.transcriptId
      )
    },
    title() {
      if (!this.transcript) return 'Detalhe da transcrição'
      return this.transcript.title || this.transcript.meeting_id || 'Sem título'
    },
    startTimeFormatted() {
      if (!this.transcript) return '-'
      return formatBackendDateTime(this.transcript.start_time)
    },
    duration() {
      if (!this.transcript || !this.transcript.start_time) return '-'
      if (!this.transcript.end_time) return 'Em andamento'
      const diffSeconds = backendDurationSeconds(
        this.transcript.start_time,
        this.transcript.end_time
      )
      if (diffSeconds === null) return '-'
      if (diffSeconds >= 3600) {
        return formatSecondsToDuration(diffSeconds, { alwaysShowHours: true })
      }
      const mm = Math.floor(diffSeconds / 60)
      const ss = diffSeconds % 60
      return mm + ' min ' + ss + ' seg'
    },
    platform() {
      return (this.transcript && this.transcript.platform) || ''
    },
    transcriptStatus() {
      return (this.transcript && this.transcript.status) || ''
    },
    aiStatus() {
      return (this.transcript && this.transcript.ai_status) || ''
    },
    summary() {
      return (this.transcript && this.transcript.summary) || null
    },
    detailedSummary() {
      if (!this.summary) return []
      const decisions = this.summary.decisions || []
      if (decisions.length) return decisions
      return this.summary.next_steps || []
    },
    generatedActions() {
      if (!this.summary) return []
      return this.summary.next_steps || []
    },
    participants() {
      return (this.transcript && this.transcript.participants) || []
    },
    canRegenerate() {
      return !this.regenerating && this.transcriptStatus === 'ended'
    },
    // Download é ação da reunião inteira, não de uma aba — só depende de
    // existir ao menos 1 segmento capturado (mesmo em reunião ao vivo).
    canDownloadTranscricao() {
      return Boolean(
        this.transcript &&
          this.transcript.segments &&
          this.transcript.segments.length
      )
    },
  },
  created() {
    GlobalBus.$on(MODAL.TRANSCRIPT_DETAIL, this.open)
    // Após salvar (cliente, projeto, nome, descrição) em outro modal, recarrega o
    // detalhe aberto silenciosamente para refletir a mudança. RELOAD.TRANSCRIPT
    // é broadcast (não abre modal) — mantém o contrato de abertura desta modal
    // restrito a um único evento MODAL.<NAME> (design-system/refactor-rules.md #17).
    GlobalBus.$on(RELOAD.TRANSCRIPT, this.pollDetail)
    this.busModal.$on('close', this.onClose)
    document.addEventListener('visibilitychange', this.handleVisibility)
  },
  beforeDestroy() {
    GlobalBus.$off(MODAL.TRANSCRIPT_DETAIL, this.open)
    GlobalBus.$off(RELOAD.TRANSCRIPT, this.pollDetail)
    this.busModal.$off('close', this.onClose)
    document.removeEventListener('visibilitychange', this.handleVisibility)
    this.stopPolling()
  },
  methods: {
    clear() {
      this.transcript = null
      this.loading = false
      this.regenerating = false
      this.loadingDownload = false
      this.tab = 'resumo'
      this.stopPolling()
    },
    open({ id } = {}) {
      this.clear()
      this.transcriptId = id || null
      this.busModal.$emit('dialog', true)
      if (this.transcriptId) this.loadDetail()
    },
    onClose() {
      this.stopPolling()
    },
    async loadDetail() {
      if (!this.transcriptId) return
      this.loading = true
      try {
        const r = await apiCrmTranscricoes.detalhar({ id: this.transcriptId })
        if (r && r.status === 200) {
          this.transcript = r.value
          if (this.transcript && this.transcript.status === 'in_progress') {
            // Reunião em andamento: o Resumo ainda não existe (gerado só ao
            // final). Abre direto na Transcrição, que tem conteúdo ao vivo.
            this.tab = 'transcricao'
            this.startPolling()
          } else {
            // Reunião finalizada: abre no Resumo (visão executiva).
            this.tab = 'resumo'
            this.stopPolling()
          }
          // Avaliação por critério (aba Avaliação) — carregamento independente.
          this.loadScores()
        } else {
          this.$showSnack(r)
        }
      } finally {
        this.loading = false
      }
    },
    // Scores por critério (spec-004). Combinados na aba com o agregado do
    // transcript (project_score/reason/improvement). A busca + cache + dispatch
    // ficam na API (apiCrmTranscricoes.carregarScores); o componente apenas chama
    // e lê pelos getters. Não-fatal: falha deixa a lista vazia (estado vazio).
    loadScores() {
      if (!this.transcriptId) return
      apiCrmTranscricoes.carregarScores({ id: this.transcriptId })
    },
    startPolling() {
      if (this.pollHandle) return
      this.pollHandle = setInterval(() => {
        if (!document.hidden) {
          this.pollDetail()
        }
      }, POLL_INTERVAL)
    },
    stopPolling() {
      if (this.pollHandle) {
        clearInterval(this.pollHandle)
        this.pollHandle = null
      }
    },
    handleVisibility() {
      // o tick já checa document.hidden a cada execução — nada extra aqui
    },
    async pollDetail() {
      if (!this.transcriptId) return
      const r = await apiCrmTranscricoes.detalhar({ id: this.transcriptId })
      if (r && r.status === 200) {
        this.transcript = r.value
        if (this.transcript && this.transcript.status !== 'in_progress') {
          this.stopPolling()
        }
      } else {
        this.$showSnack(r)
      }
    },
    // Geração é síncrona sobre dados em memória, mas o clique ainda precisa
    // do feedback visual padrão de download (CLAUDE.md § UX Principle) e de
    // não falhar em silêncio se o Blob/createObjectURL não estiver disponível.
    async handleDownloadTxt() {
      if (this.loadingDownload) return
      this.loadingDownload = true
      try {
        baixarTranscricaoTxt({
          transcript: this.transcript,
          dataFormatada: this.startTimeFormatted,
          duracao: this.duration,
        })
        await new Promise((resolve) =>
          setTimeout(resolve, MIN_DOWNLOAD_LOADING_MS)
        )
        this.$showSnack({ status: 200, message: 'Transcrição baixada.' })
      } catch (error) {
        this.$showSnack({
          status: 500,
          message: 'Não foi possível baixar a transcrição.',
        })
      } finally {
        this.loadingDownload = false
      }
    },
    async handleRegenerate() {
      if (this.regenerating) return
      this.regenerating = true
      const r = await apiCrmTranscricoes.regenerarSummary({
        id: this.transcript.id,
      })
      this.regenerating = false
      this.$showSnack(r)
      if (r && r.status === 200) {
        await this.loadDetail()
      }
    },
  },
}
</script>

<template>
  <BaseModal :bus="busModal" max-width="60%" close-color="#5B5B5B" fixedActions>
    <template v-slot:title>
      <!-- Title sem `underlined` de propósito: a barra decorativa ocupa 88% da
           largura do bloco e passaria por baixo do badge da plataforma. -->
      <div class="d-flex align-center az-gap-small mb-4">
        <Title :title="title" removeVerticalMargin />
        <TranscriptPlatformBadge :platform="platform" />
      </div>
    </template>

    <!-- Modais dedicados (abrem via GlobalBus) -->
    <TranscriptClientModal />
    <TranscriptLinkProjectModal />
    <TranscriptParticipantsModal />

    <div class="transcript-detail-modal pa-2">
      <!-- Loading skeleton no primeiro carregamento -->
      <div v-if="loading && !transcript">
        <div class="mb-4">
          <div
            class="transcript-detail-modal__skeleton-bar transcript-detail-modal__skeleton-bar--heading mb-3"
          ></div>
          <div
            class="transcript-detail-modal__skeleton-bar transcript-detail-modal__skeleton-bar--medium mb-2"
          ></div>
          <div
            class="transcript-detail-modal__skeleton-bar transcript-detail-modal__skeleton-bar--short mb-2"
          ></div>
        </div>
        <div
          v-for="n in 5"
          :key="n"
          class="transcript-detail-modal__skeleton-bar transcript-detail-modal__skeleton-bar--wide mb-3"
        ></div>
      </div>

      <template v-else>
        <div v-if="transcript">
          <DetailMeetingHeader
            ref="meetingHeader"
            :transcript="transcript"
            :start-time-formatted="startTimeFormatted"
            :duration="duration"
            :regenerating="regenerating"
            @regenerate="handleRegenerate"
          />

          <!-- Abas -->
          <v-tabs
            v-model="tab"
            class="transcript-detail-modal__tabs mt-4 mb-4"
            show-arrows
          >
            <v-tab href="#resumo" class="text-none">Resumo</v-tab>
            <v-tab href="#sentimento" class="text-none">Sentimento</v-tab>
            <v-tab href="#acoes" class="text-none">Ações geradas</v-tab>
            <v-tab href="#avaliacao" class="text-none">Avaliação</v-tab>
            <v-tab href="#participantes" class="text-none">Participantes</v-tab>
            <v-tab href="#transcricao" class="text-none">Transcrição</v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab">
            <v-tab-item value="resumo">
              <DetailTabResumo
                :summary="summary"
                :ai-status="aiStatus"
                :transcript-status="transcriptStatus"
                :loading="loading"
                :can-regenerate="canRegenerate"
                @regenerate="handleRegenerate"
              />
            </v-tab-item>

            <v-tab-item value="sentimento">
              <DetailTabSentimento :transcript="transcript" />
            </v-tab-item>

            <v-tab-item value="acoes">
              <DetailTabAcoes
                :actions="generatedActions"
                :ai-status="aiStatus"
              />
            </v-tab-item>

            <v-tab-item value="avaliacao">
              <DetailTabAvaliacao
                :transcript="transcript"
                :scores="scores"
                :loading="loadingScores"
              />
            </v-tab-item>

            <v-tab-item value="participantes">
              <DetailTabParticipantes
                :transcript-id="transcriptId"
                :participants="participants"
              />
            </v-tab-item>

            <v-tab-item value="transcricao">
              <DetailTabTranscricao :transcript="transcript" />
            </v-tab-item>
          </v-tabs-items>
        </div>

        <div v-else class="d-flex flex-column align-center py-8">
          <icon-svg
            name="mdi-alert-circle-outline"
            :size="40"
            color="#9E9E9E"
          />
          <p class="transcript-detail-modal__empty mt-2 mb-0"
            >Transcrição não encontrada.</p
          >
        </div>
      </template>
    </div>

    <template v-slot:actions="{ close }">
      <!-- mt-4 no container: margem direta em BaseButton irmão é proibida. -->
      <div class="d-flex align-center justify-end az-gap-small mt-4">
        <BaseButton underlined @click="close">Fechar</BaseButton>
        <BaseButton
          v-if="canDownloadTranscricao"
          outline
          fitContent
          :loading="loadingDownload"
          @click="handleDownloadTxt"
        >
          Baixar transcrição (.txt)
        </BaseButton>
        <BaseButton
          v-if="canRegenerate"
          primary
          fitContent
          :loading="regenerating"
          @click="handleRegenerate"
        >
          Gerar novamente
        </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<style lang="sass" scoped>
.transcript-detail-modal
  &__empty
    font-family: 'Archivo', sans-serif
    font-size: 13px
    color: var(--muted)

  &__skeleton-bar
    background: linear-gradient(90deg, #E8EAF1 25%, #D0D3DC 50%, #E8EAF1 75%)
    background-size: 200% 100%
    border-radius: 4px
    animation: skeleton-shimmer 1.5s infinite

    &--heading
      height: 22px
      width: 60%

    &--wide
      height: 14px
      width: 90%

    &--medium
      height: 14px
      width: 70%

    &--short
      height: 14px
      width: 45%

@keyframes skeleton-shimmer
  0%
    background-position: 200% 0
  100%
    background-position: -200% 0
</style>
